// /opt/isp_erp/frontend/src/components/warehouse/IngresosMaterialesPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, ButtonGroup, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Alert, CircularProgress, TablePagination,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, Autocomplete, Divider, InputAdornment
} from '@mui/material';
import { Add, Edit, Block, Refresh, Search, PictureAsPdf } from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';

const pad = (n) => String(n).padStart(2, '0');
const fechaLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const aInput = (d) => `${fechaLocal(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const fmtNum = (n) => {
  const x = Number(n || 0);
  return Number.isInteger(x) ? String(x) : x.toFixed(2);
};
const fmtDinero = (n) => (n === null || n === undefined ? '—' : `$${Number(n).toFixed(2)}`);
const fechaCorta = (s) => (s ? String(s).slice(0, 10).split('-').reverse().join('/') : '');
const etiquetaMaterial = (m) => `${m.codigo} — ${m.nombre}${m.proveedor ? ` (${m.proveedor})` : ''}`;
const recorte = {
  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: 240
};

const detalleError = (err, porDefecto) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  return porDefecto;
};

const FORM_VACIO = () => ({
  material: null, cantidad: '', costo_unitario: '', documento: '', descripcion: '', fecha: aInput(new Date())
});

const Kpi = ({ titulo, valor, detalle, pequeno }) => (
  <Paper
    variant="outlined"
    sx={{ p: 2, width: '100%', minHeight: 104, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
  >
    <Typography variant="caption" color="text.secondary">{titulo}</Typography>
    <Typography variant={pequeno ? 'h6' : 'h5'} fontWeight="bold" sx={{ lineHeight: 1.3, wordBreak: 'break-word' }}>
      {valor}
    </Typography>
    <Typography variant="caption" color="text.secondary">{detalle || '\u00A0'}</Typography>
  </Paper>
);

const IngresosMaterialesPanel = () => {
  const hoy = fechaLocal(new Date());
  const [desde, setDesde] = useState(`${hoy.slice(0, 8)}01`);
  const [hasta, setHasta] = useState(hoy);
  const [texto, setTexto] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [proveedorFiltro, setProveedorFiltro] = useState('');
  const [incluirAnulados, setIncluirAnulados] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, valor_total: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [descargando, setDescargando] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [fechaOriginal, setFechaOriginal] = useState('');
  const [form, setForm] = useState(FORM_VACIO());
  const [guardando, setGuardando] = useState(false);
  const [errorDialogo, setErrorDialogo] = useState('');
  const [opcionesMat, setOpcionesMat] = useState([]);
  const [busquedaMat, setBusquedaMat] = useState('');

  const [anulando, setAnulando] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [errorAnular, setErrorAnular] = useState('');

  useEffect(() => {
    warehouseAPI.opcionesProveedores().then((r) => setProveedores(r.data || [])).catch(() => setProveedores([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setBusqueda(texto.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [texto]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page: page + 1, page_size: rowsPerPage, incluir_anulados: incluirAnulados };
      if (desde) params.fecha_desde = desde;
      if (hasta) params.fecha_hasta = hasta;
      if (busqueda) params.q = busqueda;
      if (proveedorFiltro !== '') params.proveedor_id = proveedorFiltro;
      const r = await warehouseAPI.listarIngresosMateriales(params);
      setData(r.data);
    } catch (err) {
      setData({ items: [], total: 0, valor_total: 0 });
      setError(detalleError(err, 'Error al cargar los ingresos'));
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, busqueda, proveedorFiltro, incluirAnulados, page, rowsPerPage]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Búsqueda de materiales en el servidor (el catálogo puede tener decenas de miles)
  useEffect(() => {
    if (!dialogOpen || editando) return undefined;
    const t = setTimeout(async () => {
      try {
        const r = await warehouseAPI.listarMaterialesPaginado({
          q: busquedaMat || undefined, page_size: 20, orden: 'nombre', direccion: 'asc'
        });
        setOpcionesMat(r.data.items || []);
      } catch (err) {
        setOpcionesMat([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [busquedaMat, dialogOpen, editando]);

  const avisar = (txt) => {
    setMensaje(txt);
    setTimeout(() => setMensaje(''), 5000);
  };

  const bajarPDF = async (params, nombre) => {
    setDescargando(true);
    try {
      const r = await warehouseAPI.descargarIngresosPDF(params);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombre);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let texto = 'No se pudo generar el PDF';
      try {
        const json = JSON.parse(await err.response.data.text());
        if (typeof json.detail === 'string') texto = json.detail;
      } catch (e) {
        /* sin detalle del servidor */
      }
      setError(texto);
    } finally {
      setDescargando(false);
    }
  };

  const descargarPDF = () => {
    const params = { tipo: 'materiales', incluir_anulados: incluirAnulados };
    if (desde) params.fecha_desde = desde;
    if (hasta) params.fecha_hasta = hasta;
    if (busqueda) params.q = busqueda;
    if (proveedorFiltro !== '') params.proveedor_id = proveedorFiltro;
    bajarPDF(params, `ingresos_materiales_${desde || 'inicio'}_${hasta || 'hoy'}.pdf`);
  };

  const rango = (tipo) => {
    setPage(0);
    if (tipo === 'hoy') {
      setDesde(fechaLocal(new Date()));
      setHasta(fechaLocal(new Date()));
    } else if (tipo === 'mes') {
      const h = fechaLocal(new Date());
      setDesde(`${h.slice(0, 8)}01`);
      setHasta(h);
    } else {
      setDesde('');
      setHasta('');
    }
  };

  const abrirNuevo = () => {
    setEditando(null);
    setForm(FORM_VACIO());
    setBusquedaMat('');
    setErrorDialogo('');
    setDialogOpen(true);
  };

  const abrirEditar = (i) => {
    const fecha = aInput(new Date(i.fecha_ingreso));
    setEditando(i);
    setFechaOriginal(fecha);
    setForm({
      material: { id: i.material_id, codigo: i.material_codigo, nombre: i.material_nombre, proveedor: i.proveedor, unidad: i.unidad },
      cantidad: String(i.cantidad),
      costo_unitario: i.costo_unitario === null || i.costo_unitario === undefined ? '' : String(i.costo_unitario),
      documento: i.documento || '',
      descripcion: i.descripcion || '',
      fecha
    });
    setErrorDialogo('');
    setDialogOpen(true);
  };

  const guardar = async () => {
    const cantidad = parseFloat(form.cantidad);
    if (!editando && !form.material) {
      setErrorDialogo('Selecciona un material');
      return;
    }
    if (!(cantidad > 0)) {
      setErrorDialogo('La cantidad debe ser mayor a 0');
      return;
    }
    setGuardando(true);
    setErrorDialogo('');
    try {
      let r;
      if (editando) {
        const cambios = {
          cantidad,
          costo_unitario: form.costo_unitario === '' ? null : parseFloat(form.costo_unitario),
          documento: form.documento,
          descripcion: form.descripcion
        };
        if (form.fecha !== fechaOriginal) cambios.fecha_ingreso = form.fecha;
        r = await warehouseAPI.editarIngresoMaterial(editando.id, cambios);
      } else {
        const cuerpo = {
          material_id: form.material.id,
          cantidad,
          documento: form.documento,
          descripcion: form.descripcion,
          fecha_ingreso: form.fecha
        };
        if (form.costo_unitario !== '') cuerpo.costo_unitario = parseFloat(form.costo_unitario);
        r = await warehouseAPI.registrarIngresoMaterial(cuerpo);
      }
      setDialogOpen(false);
      avisar(`${r.data.message}. Stock en bodega: ${fmtNum(r.data.stock_bodega)}`);
      cargar();
    } catch (err) {
      setErrorDialogo(detalleError(err, 'No se pudo guardar el ingreso'));
    } finally {
      setGuardando(false);
    }
  };

  const confirmarAnular = async () => {
    if (motivo.trim().length < 3) {
      setErrorAnular('Indica el motivo de la anulación');
      return;
    }
    try {
      const r = await warehouseAPI.anularIngresoMaterial(anulando.id, { motivo: motivo.trim() });
      setAnulando(null);
      setMotivo('');
      avisar(`${r.data.message}. Stock en bodega: ${fmtNum(r.data.stock_bodega)}`);
      cargar();
    } catch (err) {
      setErrorAnular(detalleError(err, 'No se pudo anular el ingreso'));
    }
  };

  const cambiar = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h6">Ingresos de materiales</Typography>
            <Typography variant="body2" color="text.secondary">
              Cada aumento de stock queda registrado con fecha, hora y usuario. Puedes editarlo o anularlo si te equivocaste.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={descargando ? <CircularProgress size={16} /> : <PictureAsPdf />}
              onClick={descargarPDF}
              disabled={descargando}
            >
              Descargar PDF
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={abrirNuevo}>
              Nuevo ingreso
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <Kpi titulo="Ingresos en el período" valor={data.total} detalle="Registros que cumplen el filtro" />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <Kpi titulo="Valor total" valor={fmtDinero(data.valor_total)} detalle="Solo ingresos con costo registrado" />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <Kpi
              titulo="Período"
              valor={`${fechaCorta(desde) || 'Inicio'} – ${fechaCorta(hasta) || 'Hoy'}`}
              detalle="Hora de Ecuador"
              pequeno
            />
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={2.5}>
              <TextField fullWidth type="date" size="small" label="Desde" value={desde} onChange={cambiar(setDesde)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <TextField fullWidth type="date" size="small" label="Hasta" value={hasta} onChange={cambiar(setHasta)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <ButtonGroup size="small" variant="outlined">
                <Button onClick={() => rango('hoy')}>Hoy</Button>
                <Button onClick={() => rango('mes')}>Este mes</Button>
                <Button onClick={() => rango('todo')}>Todo</Button>
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button fullWidth size="small" variant="outlined" startIcon={<Refresh />} onClick={cargar}>
                Actualizar
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Proveedor</InputLabel>
                <Select label="Proveedor" value={proveedorFiltro} onChange={cambiar(setProveedorFiltro)}>
                  <MenuItem value="">Todos</MenuItem>
                  {proveedores.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Buscar material, factura o descripción"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={<Switch checked={incluirAnulados} onChange={(e) => { setIncluirAnulados(e.target.checked); setPage(0); }} />}
                label="Incluir anulados"
              />
            </Grid>
          </Grid>
        </Paper>

        {mensaje && <Alert severity="success" sx={{ mb: 2 }}>{mensaje}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Fecha y registro</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Material</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Proveedor y factura</TableCell>
                <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}>Cantidad</TableCell>
                <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}>Costo</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Descripción</TableCell>
                <TableCell align="center" sx={{ bgcolor: '#f5f5f5' }}>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell>
                </TableRow>
              ) : data.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">No hay ingresos en este período</TableCell>
                </TableRow>
              ) : (
                data.items.map((i) => {
                  const f = new Date(i.fecha_ingreso);
                  return (
                    <TableRow key={i.id} hover sx={{ opacity: i.anulado ? 0.55 : 1 }}>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Typography variant="body2" fontWeight="bold">{f.toLocaleDateString('es-EC')}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {f.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })} · {i.usuario || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ textDecoration: i.anulado ? 'line-through' : 'none' }}>
                          {i.material_nombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">{i.material_codigo}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        <Typography variant="body2">{i.proveedor || 'Sin proveedor'}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Factura: {i.documento || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight="bold">{fmtNum(i.cantidad)}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">{i.unidad}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">{fmtDinero(i.total)}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Unit.: {fmtDinero(i.costo_unitario)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={i.descripcion || ''} disableHoverListener={!i.descripcion}>
                          <Typography variant="body2" sx={recorte}>{i.descripcion || '—'}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        {i.anulado ? (
                          <Tooltip title={`Anulado por ${i.anulado_por || '—'}: ${i.motivo_anulacion || ''}`}>
                            <Chip size="small" label="Anulado" />
                          </Tooltip>
                        ) : (
                          <>
                            <Chip size="small" color="success" label="Vigente" sx={{ mr: 0.5 }} />
                            <Tooltip title="Editar">
                              <IconButton size="small" onClick={() => abrirEditar(i)}><Edit fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Anular">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => { setAnulando(i); setMotivo(''); setErrorAnular(''); }}
                              >
                                <Block fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data.total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          onPageChange={(e, nueva) => setPage(nueva)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => !guardando && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar ingreso' : 'Nuevo ingreso de material'}</DialogTitle>
        <DialogContent dividers>
          {errorDialogo && <Alert severity="error" sx={{ mb: 2 }}>{errorDialogo}</Alert>}
          <Typography variant="subtitle2" color="primary" gutterBottom>Material</Typography>
          <Divider sx={{ mb: 2 }} />
          <Autocomplete
            size="small"
            disabled={!!editando}
            options={editando && form.material ? [form.material] : opcionesMat}
            value={form.material}
            getOptionLabel={etiquetaMaterial}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(x) => x}
            onChange={(e, valor) => setForm({ ...form, material: valor })}
            onInputChange={(e, valor, razon) => {
              if (razon === 'input') setBusquedaMat(valor);
            }}
            noOptionsText="Escribe para buscar por código o nombre"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Material *"
                helperText={editando ? 'Para cambiar de material, anula este ingreso y registra uno nuevo' : (form.material ? `Unidad: ${form.material.unidad}` : '')}
              />
            )}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle2" color="primary" gutterBottom>Cantidad y costo</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Cantidad *" value={form.cantidad} onChange={(e) => setForm({ ...form, cantidad: e.target.value })} inputProps={{ min: 0, step: 'any' }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Costo unitario (opcional)" value={form.costo_unitario} onChange={(e) => setForm({ ...form, costo_unitario: e.target.value })} inputProps={{ min: 0, step: 'any' }} />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" color="primary" gutterBottom>Documento y fecha</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Factura / documento" value={form.documento} onChange={(e) => setForm({ ...form, documento: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                label="Fecha y hora"
                value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: aInput(new Date()) }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Descripción" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={guardando}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!anulando} onClose={() => setAnulando(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Anular ingreso</DialogTitle>
        <DialogContent>
          {errorAnular && <Alert severity="error" sx={{ mb: 2 }}>{errorAnular}</Alert>}
          <Typography sx={{ mb: 2 }}>
            Se descontarán <strong>{anulando ? fmtNum(anulando.cantidad) : ''} {anulando?.unidad}</strong> de{' '}
            <strong>{anulando?.material_nombre}</strong> del stock de bodega. El ingreso queda en el historial como anulado.
          </Typography>
          <TextField fullWidth multiline rows={2} label="Motivo de la anulación *" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnulando(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmarAnular}>Anular ingreso</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default IngresosMaterialesPanel;
